import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Segmented,
  Select,
  Spin,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { UiDataTable } from "../ui";
import {
  floorListApi,
  getNurseIpPatientApi,
} from "../../services/nurse.services";
import { getCookie } from "../../helpers/localStorage";
import { FacilityID } from "../../constants/defaultKeys";
import { UserContext } from "../../context/UserContext";
import "./NurseView.css";
import NoDataPage from "../NoDataPages/NoDataPage";
import LoadingIndicator from "../Loaders/LoadingIndicator";
import patientDataIcon from "../../assets/images/nurseIcons/patientdata.svg";
import useQuery from "../../hooks/useQuery";
import {
  updateUrlSearchParams,
  parsePageParam,
  parseSearchParam,
  parseFloorParam,
} from "../../helpers/urlFilters";
import NursePatientTable from "./NursePatientTable";

const { Option } = Select;
const inPatientLabels = [
  { label: "Active", value: "active" },
  { label: "Discharged", value: "discharged" },
];

const NurseCards = () => {
  const { userDetails } = useContext(UserContext);
  const params = useQuery();
  const navigate = useNavigate();
  const [activeSegment, setActiveSegment] = useState(
    params?.status === "current" ? "active" : "discharged"
  );
  const [ipData, setIpData] = useState([]);
  const [floor, setFloor] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState(() => parseFloorParam(params, 0));
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [segmentCounts, setSegmentCounts] = useState({
    active: null,
    discharged: null,
  });

  const [pageNo, setPageNo] = useState(() => parsePageParam(params));
  const [searchQuery, setSearchQuery] = useState(() => parseSearchParam(params));

  const facilityId =
    Number(getCookie(FacilityID)) || userDetails?.currFacility;

  useEffect(() => {
    setSegmentCounts({ active: null, discharged: null });
  }, [facilityId, selectedFloor, searchQuery]);

  useEffect(() => {
    const query = searchQuery
      ? `&search=${searchQuery}&page=1`
      : `&page=${pageNo}`;
    const listParams =
      selectedFloor === 0
        ? `?facility=${facilityId}&list=${activeSegment}${query}`
        : `?facility=${facilityId}&floor=${selectedFloor}&list=${activeSegment}${query}`;

    setLoadingPatients(true);

    getNurseIpPatientApi(listParams)
      .then((res) => {
        setIpData(res?.data);
        setSegmentCounts((prev) => ({
          ...prev,
          [activeSegment]: res?.data?.total_count ?? 0,
        }));
      })
      .catch((err) => console.log(err))
      .finally(() => setLoadingPatients(false));
  }, [facilityId, selectedFloor, activeSegment, searchQuery, pageNo]);

  useEffect(() => {
    floorListApi(`?facility=${facilityId}`)
      .then((res) => {
        setFloor(res?.data);
      })
      .catch((err) => console.log(err));
  }, [facilityId]);

  const onFloorChange = (value) => {
    setPageNo(1);
    setSelectedFloor(value);
    updateUrlSearchParams(navigate, { floor: value || null, page: null });
  };

  const onPatientTypeChange = (value) => {
    if (value === activeSegment) return;
    updateUrlSearchParams(navigate, {
      status: value === "active" ? "current" : "older",
      search: null,
      page: null,
    });
    setLoadingPatients(true);
    setIpData({ data: [], total_count: 0 });
    setSearchQuery(null);
    setActiveSegment(value);
    setPageNo(1);
  };

  const handlePatientClick = (item, type, care = false) => {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set("patient", item?.admission_id);
    newUrl.searchParams.set("type", type);
    care && newUrl.searchParams.set("care", care);
    navigate(`${newUrl.pathname}${newUrl.search}`, { replace: true });
  };

  const onContactSearch = (value) => {
    const query = value?.target?.value;
    setSearchQuery(query || null);
    setPageNo(1);
    updateUrlSearchParams(navigate, { search: query || null, page: null });
  };

  const handlePageChange = (value) => {
    setPageNo(value);
    updateUrlSearchParams(navigate, { page: value === 1 ? null : value });
  };

  const inPatientLabelsWithCount = inPatientLabels.map((option) => {
    const count = segmentCounts[option.value];
    const isActiveOption = option.value === activeSegment;
    const showLoading = isActiveOption && count == null;

    return {
      ...option,
      label: showLoading ? (
        <span className="patient-list-segment-label">
          {option.label}{" "}
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 12 }} spin />}
            size="small"
          />
        </span>
      ) : count == null ? (
        option.label
      ) : (
        `${option.label} (${count})`
      ),
    };
  });

  const truncateName = (name, role) => {
    if (role === "patient") {
      return name && name.length > 20 ? `${name.slice(0, 20)}...` : name;
    } else if (role === "consultant") {
      return name && name.length > 12 ? `${name.slice(0, 12)}...` : name;
    }
    return name;
  };

  const handlePatientViewClick = (item) => {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set("patient", item?.admission_id);
    newUrl.searchParams.set("type", "ia");
    navigate(`${newUrl.pathname}${newUrl.search}`, { replace: true });
  };

  const listFilters = (
    <div className="nurse-cards-toolbar-filters">
      <Select
        className="patient-floor-select"
        style={{ width: 200, textAlign: "left" }}
        placeholder="Select Floor"
        value={selectedFloor}
        onChange={onFloorChange}
        optionLabelProp="label"
        popupClassName="patient-floor-select-dropdown"
        popupMatchSelectWidth={false}
      >
        <Option value={0} label="All Floors">
          All Floors
        </Option>
        {floor?.map((item) => (
          <Option
            key={item?.id}
            value={item?.id}
            label={`${item?.floor_no} - ${item?.building_name}`}
            title={`${item?.floor_no} - ${item?.building_name}`}
          >
            {`${item?.floor_no} - ${item?.building_name}`}
          </Option>
        ))}
      </Select>
      <Segmented
        key={`nurse-ip-segment-${activeSegment}-${segmentCounts[activeSegment] ?? "loading"}`}
        className="patient-list-segment nurse-patient-segment patient-list-segment--counts"
        options={inPatientLabelsWithCount}
        value={activeSegment}
        onChange={onPatientTypeChange}
      />
    </div>
  );

  return (
    <UiDataTable
      showHeader={false}
      shellClassName="ui-table-fill-shell nurse-cards-shell"
      onSearch={(e) => onContactSearch(e)}
      searchPlaceholder="Search for patient"
      toolbarTrailing={listFilters}
    >
      {loadingPatients ? (
        <LoadingIndicator message={"Loading patient data, please wait..."} />
      ) : ipData?.data?.length > 0 ? (
        <NursePatientTable
          dataSource={ipData?.data}
          totalCount={ipData?.total_count ?? 0}
          loading={loadingPatients}
          activeSegment={activeSegment}
          pageNo={pageNo}
          onPageChange={handlePageChange}
          onPatientClick={handlePatientClick}
          onPatientViewClick={handlePatientViewClick}
          truncateName={truncateName}
        />
      ) : (
        <NoDataPage
          title={"No Patient Data"}
          subtitle={"Seems like, there are'nt any patient data"}
          image={patientDataIcon}
        />
      )}
    </UiDataTable>
  );
};

export default NurseCards;
